"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useState, type ReactNode } from "react";
import { Box, Button, Container, Divider, Drawer, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { Clapperboard, ClipboardList, Film, FolderOpen, Home, LogOut, Menu, Moon, RadioTower, Scissors, Settings, Sun, Video, X } from "lucide-react";

import { useAppColorMode } from "@/app/theme-provider";
import { logout } from "@/features/auth/api";

const navItems = [
  { label: "Home", href: "/", icon: Home },
  { label: "Sources", href: "/sources", icon: Video },
  { label: "Editor", href: "/editor", icon: Scissors },
  { label: "Channels", href: "/content-profiles", icon: Clapperboard },
  { label: "Videos", href: "/video-jobs", icon: Film },
  { label: "Asset library", href: "/asset-library", icon: FolderOpen },
  { label: "Review log", href: "/review-log", icon: ClipboardList },
  { label: "Publishing", href: "/publishing", icon: RadioTower },
  { label: "Settings", href: "/settings", icon: Settings }
];

export function AppShell({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const { mode, toggleMode } = useAppColorMode();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout().catch(() => {});
    router.replace("/login");
    router.refresh();
  };

  return (
    <>
      <Box
        component="header"
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1200,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: mode === "dark" ? "rgba(9,11,16,0.82)" : "rgba(244,245,248,0.84)",
          backdropFilter: "blur(18px)"
        }}
      >
        <Container maxWidth="xl">
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ height: 68 }}>
            <Stack component={Link} href="/" direction="row" spacing={1.25} alignItems="center" sx={{ color: "text.primary", textDecoration: "none" }}>
              <Box
                sx={{
                  width: 34,
                  height: 34,
                  borderRadius: 1.5,
                  display: "grid",
                  placeItems: "center",
                  color: "white",
                  fontWeight: 850,
                  background: "linear-gradient(145deg, #9a8cff, #6552dc)",
                  boxShadow: "0 8px 24px rgba(109,91,232,.32)"
                }}
              >
                A
              </Box>
              <Typography fontWeight={850} letterSpacing="-0.03em">Atelier</Typography>
            </Stack>

            <Stack direction="row" spacing={0.5} sx={{ display: { xs: "none", md: "flex" } }}>
              {navItems.map((item) => {
                const active = item.href === "/" ? pathname === "/" : pathname.startsWith(item.href);
                const Icon = item.icon;
                return (
                  <Button
                    key={item.href}
                    component={Link}
                    href={item.href}
                    color={active ? "primary" : "inherit"}
                    variant={active ? "outlined" : "text"}
                    startIcon={<Icon size={17} />}
                    sx={{ minHeight: 38, borderColor: active ? "rgba(139,124,246,.35)" : "transparent" }}
                  >
                    {item.label}
                  </Button>
                );
              })}
            </Stack>

            <Stack direction="row" spacing={0.5}>
              <Tooltip title={`Use ${mode === "dark" ? "light" : "dark"} theme`}>
                <IconButton aria-label="Toggle theme" onClick={toggleMode}>
                  {mode === "dark" ? <Sun size={19} /> : <Moon size={19} />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Log out">
                <IconButton aria-label="Log out" onClick={handleLogout}>
                  <LogOut size={19} />
                </IconButton>
              </Tooltip>
              <IconButton aria-label="Open navigation" onClick={() => setMobileOpen(true)} sx={{ display: { md: "none" } }}>
                <Menu size={21} />
              </IconButton>
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Drawer anchor="right" open={mobileOpen} onClose={() => setMobileOpen(false)}>
        <Stack spacing={2} sx={{ width: 292, p: 2.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography fontWeight={850}>Navigation</Typography>
            <IconButton aria-label="Close navigation" onClick={() => setMobileOpen(false)}><X size={20} /></IconButton>
          </Stack>
          <Divider />
          {navItems.map((item) => {
            const active = item.href === "/" ? pathname === "/" : pathname.startsWith(item.href);
            const Icon = item.icon;
            return (
              <Button
                key={item.href}
                component={Link}
                href={item.href}
                onClick={() => setMobileOpen(false)}
                variant={active ? "contained" : "text"}
                color={active ? "primary" : "inherit"}
                startIcon={<Icon size={18} />}
                sx={{ justifyContent: "flex-start" }}
              >
                {item.label}
              </Button>
            );
          })}
        </Stack>
      </Drawer>

      {children}
    </>
  );
}
